import { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CustomButton from "../CustomButton/CustomButton";
import SearchBox from "../SearchBox/SearchBox";
import { formatDisplayDate, getApiErrorMessage } from "../../services/apiClient";
import {
  buildSrtContent,
  buildVttContent,
  downloadTextFile,
  getPhoneticDownloadBaseName,
  getSubtitleBaseName,
  isNoAudioTranscript,
  NO_AUDIO_DETECTED_MESSAGE,
  normalizePhoneticSegments,
} from "../../util/subtitleHelpers";
import {
  fetchAllCaptionJobs,
  fetchCaptionJob,
  fetchCaptionJobVideo,
} from "../../services/captionPublishingApi";
import { assertAdminAuth } from "../../services/apiClient";
import GoogleLoginHistoryPanel from "../GoogleLoginHistoryPanel/GoogleLoginHistoryPanel";
import CaptionConsumptionAlertsPanel from "../CaptionConsumptionAlertsPanel/CaptionConsumptionAlertsPanel";
import "./CaptionPublishingAdminTab.scss";

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "completed", label: "Success" },
  { id: "failed", label: "Failed" },
];

const getJobUser = (job) => job?.user || job?.googleUser || {};

const getJobSearchText = (job) => {
  const user = getJobUser(job);

  return [
    user.name,
    user.email,
    job.title,
    job.videoFileName,
    job.jobId,
    job.status,
    job.error,
    job.message,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const isJobWithinDateRange = (job, startDate, endDate) => {
  if (!startDate && !endDate) {
    return true;
  }

  const jobDate = job.createdAt ? new Date(job.createdAt) : null;

  if (!jobDate || Number.isNaN(jobDate.getTime())) {
    return true;
  }

  if (startDate) {
    const rangeStart = new Date(startDate);
    rangeStart.setHours(0, 0, 0, 0);

    if (jobDate < rangeStart) {
      return false;
    }
  }

  if (endDate) {
    const rangeEnd = new Date(endDate);
    rangeEnd.setHours(23, 59, 59, 999);

    if (jobDate > rangeEnd) {
      return false;
    }
  }

  return true;
};

const CaptionPublishingAdminTab = () => {
  const videoPreviewRef = useRef("");
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState("");
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const resetVideoPreview = () => {
    if (videoPreviewRef.current) {
      URL.revokeObjectURL(videoPreviewRef.current);
      videoPreviewRef.current = "";
    }

    setVideoPreviewUrl("");
    setVideoError("");
  };

  const loadJobVideo = async (jobId) => {
    resetVideoPreview();
    setIsVideoLoading(true);

    try {
      const blob = await fetchCaptionJobVideo(jobId);
      const objectUrl = URL.createObjectURL(blob);
      videoPreviewRef.current = objectUrl;
      setVideoPreviewUrl(objectUrl);
    } catch (error) {
      setVideoError(getApiErrorMessage(error, "Unable to load video for this upload."));
    } finally {
      setIsVideoLoading(false);
    }
  };

  const loadJobs = async () => {
    if (!assertAdminAuth()) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const items = await fetchAllCaptionJobs();
      setJobs(items);
    } catch (error) {
      setJobs([]);
      setErrorMessage(
        getApiErrorMessage(error, "Failed to load caption publishing uploads."),
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();

    return () => {
      if (videoPreviewRef.current) {
        URL.revokeObjectURL(videoPreviewRef.current);
      }
    };
  }, []);

  const filteredJobs = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return jobs.filter((job) => {
      if (statusFilter !== "all" && job.status !== statusFilter) {
        return false;
      }

      if (!isJobWithinDateRange(job, startDate, endDate)) {
        return false;
      }

      if (!query) {
        return true;
      }

      return getJobSearchText(job).includes(query);
    });
  }, [jobs, searchQuery, startDate, endDate, statusFilter]);

  const hasActiveFilters =
    Boolean(searchQuery.trim()) ||
    Boolean(startDate) ||
    Boolean(endDate) ||
    statusFilter !== "all";

  useEffect(() => {
    if (!selectedJob) {
      return;
    }

    const isSelectedVisible = filteredJobs.some((job) => job.jobId === selectedJob.jobId);

    if (!isSelectedVisible) {
      setSelectedJob(null);
      resetVideoPreview();
    }
  }, [filteredJobs, selectedJob]);

  const clearFilters = () => {
    setSearchQuery("");
    setStartDate(null);
    setEndDate(null);
    setStatusFilter("all");
  };

  const handleSelectJob = async (job) => {
    try {
      const details = await fetchCaptionJob(job.jobId);
      setSelectedJob({ ...job, ...details });
      await loadJobVideo(job.jobId);
    } catch (error) {
      resetVideoPreview();
      window.alert(getApiErrorMessage(error, "Unable to load job details."));
    }
  };

  const selectedJobHasNoAudio =
    selectedJob &&
    isNoAudioTranscript(
      selectedJob.transcript || "",
      selectedJob.segments || [],
    );

  const downloadFromJob = (job, format, variant = "default") => {
    const baseName =
      variant === "phonetic"
        ? getPhoneticDownloadBaseName(job.videoFileName || job.title || "video")
        : getSubtitleBaseName(job.videoFileName || job.title || "video");
    const segments =
      variant === "phonetic"
        ? normalizePhoneticSegments(job.phoneticSegments || [])
        : job.segments || [];
    const content = format === "vtt" ? buildVttContent(segments) : buildSrtContent(segments);

    downloadTextFile(`${baseName}.${format}`, content, format === "vtt" ? "text/vtt" : "application/x-subrip");
  };

  return (
    <div className="caption-admin-tab">
      <div className="caption-admin-tab__header">
        <h3 className="caption-admin-tab__title">Caption Publishing uploads</h3>
        <CustomButton title="Refresh" className="submitBtn" onClick={loadJobs} />
      </div>

      <div className="caption-admin-tab__filters">
        <div className="caption-admin-tab__search">
          <SearchBox
            placeholder="Search by user, email, video, job ID, or status"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </div>

        <div className="caption-admin-tab__filter-controls">
          <div className="caption-admin-tab__date-range">
            <div className="caption-admin-tab__date-field">
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                placeholderText="Start date"
                isClearable
              />
              <i className="icon-calendar iconCalendar" aria-hidden="true"></i>
            </div>
            <div className="caption-admin-tab__date-field">
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                placeholderText="End date"
                minDate={startDate || undefined}
                isClearable
              />
              <i className="icon-calendar iconCalendar" aria-hidden="true"></i>
            </div>
          </div>

          <div className="caption-admin-tab__status-filters">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={`caption-admin-tab__status-btn${
                  statusFilter === filter.id ? " caption-admin-tab__status-btn--active" : ""
                }`}
                onClick={() => setStatusFilter(filter.id)}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {hasActiveFilters && (
            <button type="button" className="caption-admin-tab__clear-filters" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      <p className="caption-admin-tab__results">
        Showing {filteredJobs.length} of {jobs.length} uploads
      </p>

      {isLoading && <p className="caption-admin-tab__message">Loading uploads...</p>}
      {errorMessage && <p className="caption-admin-tab__error">{errorMessage}</p>}

      <div className="caption-admin-tab__layout">
        <div className="caption-admin-tab__list">
          {!isLoading && filteredJobs.length === 0 && (
            <p className="caption-admin-tab__message">No uploads match the current filters.</p>
          )}

          {filteredJobs.map((job) => {
            const user = getJobUser(job);
            const statusClassName = job.status ? `caption-admin-tab__status--${job.status}` : "";

            return (
              <button
                key={job.jobId}
                type="button"
                className={`caption-admin-tab__item ${selectedJob?.jobId === job.jobId ? "caption-admin-tab__item--active" : ""}`}
                onClick={() => handleSelectJob(job)}
              >
                <div className="caption-admin-tab__user">
                  {user.pictureUrl && (
                    <img
                      src={user.pictureUrl}
                      alt={user.name || "User"}
                      className="caption-admin-tab__avatar"
                    />
                  )}
                  <div>
                    <p className="caption-admin-tab__name">{user.name || "Unknown user"}</p>
                    <p className="caption-admin-tab__email">{user.email || "No email"}</p>
                  </div>
                </div>
                <p className="caption-admin-tab__video">{job.title || job.videoFileName}</p>
                <p className="caption-admin-tab__meta">
                  {formatDisplayDate(job.createdAt)} ·{" "}
                  <span className={`caption-admin-tab__status ${statusClassName}`}>
                    {job.status || "pending"}
                  </span>
                </p>
              </button>
            );
          })}
        </div>

        <div className="caption-admin-tab__detail">
          {!selectedJob && (
            <p className="caption-admin-tab__message">Select an upload to view details and subtitles.</p>
          )}

          {selectedJob && (
            <>
              <div className="caption-admin-tab__detail-header">
                {(selectedJob.user?.pictureUrl || selectedJob.googleUser?.pictureUrl) && (
                  <img
                    src={selectedJob.user?.pictureUrl || selectedJob.googleUser?.pictureUrl}
                    alt=""
                    className="caption-admin-tab__avatar caption-admin-tab__avatar--large"
                  />
                )}
                <div>
                  <h4>{selectedJob.user?.name || selectedJob.googleUser?.name || "User"}</h4>
                  <p>{selectedJob.user?.email || selectedJob.googleUser?.email}</p>
                  <p className="caption-admin-tab__meta">
                    {selectedJob.title || selectedJob.videoFileName} · {selectedJob.status}
                  </p>
                </div>
              </div>

              <div className="caption-admin-tab__video-section">
                <h5 className="caption-admin-tab__video-title">Uploaded video</h5>
                {isVideoLoading && (
                  <p className="caption-admin-tab__message">Loading video preview...</p>
                )}
                {videoError && !isVideoLoading && (
                  <p className="caption-admin-tab__error">{videoError}</p>
                )}
                {videoPreviewUrl && !isVideoLoading && (
                  <>
                    <p className="caption-admin-tab__video-name">
                      {selectedJob.title || selectedJob.videoFileName}
                    </p>
                    <video
                      className="caption-admin-tab__video-player"
                      src={videoPreviewUrl}
                      controls
                      playsInline
                      preload="metadata"
                    />
                  </>
                )}
              </div>

              {selectedJobHasNoAudio && (
                <p className="caption-admin-tab__no-audio">
                  <strong>{NO_AUDIO_DETECTED_MESSAGE}</strong>
                </p>
              )}

              {!selectedJobHasNoAudio && (
              <div className="caption-admin-tab__scripts">
                <div>
                  <h5>Default script</h5>
                  <textarea readOnly value={selectedJob.transcript || ""} />
                  <h5 className="caption-admin-tab__srt-title">Default .SRT preview</h5>
                  <textarea
                    readOnly
                    className="caption-admin-tab__srt"
                    value={buildSrtContent(selectedJob.segments || [])}
                  />
                  <div className="caption-admin-tab__downloads">
                    <CustomButton
                      title="Download .SRT"
                      className="submitBtn"
                      onClick={() => downloadFromJob(selectedJob, "srt", "default")}
                    />
                    <CustomButton
                      title="Download .VTT"
                      className="submitBtn"
                      onClick={() => downloadFromJob(selectedJob, "vtt", "default")}
                    />
                  </div>
                </div>

                <div>
                  <h5>Phonetic script</h5>
                  <textarea
                    readOnly
                    value={selectedJob.phoneticTranscript || "Phonetic script not available."}
                  />
                  <h5 className="caption-admin-tab__srt-title">Phonetic .SRT preview</h5>
                  <textarea
                    readOnly
                    className="caption-admin-tab__srt"
                    value={buildSrtContent(
                      normalizePhoneticSegments(selectedJob.phoneticSegments || []),
                    )}
                  />
                  <div className="caption-admin-tab__downloads">
                    <CustomButton
                      title="Download phonetic .SRT"
                      className="submitBtn"
                      onClick={() => downloadFromJob(selectedJob, "srt", "phonetic")}
                      disabled={!selectedJob.phoneticSegments?.length}
                    />
                    <CustomButton
                      title="Download phonetic .VTT"
                      className="submitBtn"
                      onClick={() => downloadFromJob(selectedJob, "vtt", "phonetic")}
                      disabled={!selectedJob.phoneticSegments?.length}
                    />
                  </div>
                </div>
              </div>
              )}
            </>
          )}
        </div>
      </div>

      <CaptionConsumptionAlertsPanel limit={20} />

      <GoogleLoginHistoryPanel mode="admin" limit={20} />
    </div>
  );
};

export default CaptionPublishingAdminTab;
